/**
 * Shared list-page helpers. Centralized so the query-string shape, the
 * page-size clamp, and the out-of-range canonicalization stay identical
 * across every paginated list — the office dashboards and the public rental
 * catalog alike.
 */

/** The page-size choices offered by every list's `PageSizeSelect`. */
export const PAGE_SIZES = [25, 50, 100, 200] as const;

/**
 * Parse a `?per=N` value, accepting it only if it's one of {@link PAGE_SIZES};
 * anything else falls back to the page's own default (lists differ — 25 or 50).
 */
export function parsePageSize(v: string | undefined, fallback: number): number {
  if (!v || !/^\d+$/.test(v)) return fallback;
  const n = Number(v);
  return (PAGE_SIZES as readonly number[]).includes(n) ? n : fallback;
}

/** The 1-based page number from a raw `?page=` value (≥1; missing/junk → 1). */
export function parsePageParam(v: string | undefined): number {
  if (!v || !/^\d+$/.test(v)) return 1;
  const page = Number(v);
  return Number.isSafeInteger(page) && page >= 1 ? page : 1;
}

/**
 * Clamp a requested page against the actual row count and derive the `?per=`
 * value to thread through pagination links. Call AFTER the count is known;
 * resolve `pageSize` with {@link parsePageSize} before the query (it's a query
 * arg). `perParam` is undefined when the size is the page's default, so links
 * stay clean.
 *
 * When the requested page was clamped, the rows in hand were fetched at an
 * invalid offset — the page MUST canonicalize rather than render an empty
 * table, so the redirect is a required argument, not a call-site convention.
 * The callback owns the full canonical URL (route + every filter param) and
 * must diverge (Next's `redirect` throws; the `never` return type enforces
 * it).
 */
export function resolvePage(opts: {
  requestedPage: number;
  total: number;
  pageSize: number;
  defaultSize: number;
  redirect: (canonical: { page: number; per: string | undefined }) => never;
}): { page: number; totalPages: number; perParam: string | undefined } {
  const totalPages = Math.max(1, Math.ceil(opts.total / opts.pageSize));
  const page = Math.min(Math.max(1, opts.requestedPage), totalPages);
  const perParam =
    opts.pageSize !== opts.defaultSize ? String(opts.pageSize) : undefined;
  if (page !== opts.requestedPage) {
    opts.redirect({ page, per: perParam });
  }
  return { page, totalPages, perParam };
}

/**
 * Build a query string from params, skipping empty / null / undefined values.
 * Returns `"?a=1&b=2"` (leading `?` included) or `""` when nothing is set, so
 * it drops straight into an `href`.
 */
export function qs(
  params: Record<string, string | number | undefined | null>,
): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "" && v !== null) sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/**
 * Query params as the office pages read them: single values. Next hands a
 * REPEATED key back as an array (`?q=a&q=b`), which a page calling `.trim()`
 * on it turns into a 500; the first value wins, so a hand-edited or shared
 * address degrades to a filter instead of an error page.
 */
export function singles(
  sp: Record<string, string | string[] | undefined>,
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [k, v] of Object.entries(sp)) out[k] = Array.isArray(v) ? v[0] : v;
  return out;
}
